// End-of-hand presentation on the table. The server deals the next hand at once, so without this
// a showdown or an all-in runout would vanish instantly. When a hand this page was watching ends,
// its public history is replayed on the felt: shown hands flip over, the remaining board cards
// come one by one (the last one slowly), the winners light up, and the result stays a moment.
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { api } from '../lib/api.ts';
import type { HandView } from '../../server/engine/view.ts';
import type { Card } from '../../shared/cards.ts';

export interface ShowdownView {
  active: boolean;
  handNo: number | null;
  /** The finished hand as it should look right now (board so far, hands flipped or not). */
  hand: HandView | null;
  potTotal: number;
  /** Seat → amount won and hand name, once the winners are shown. */
  winners: Map<number, { amount: number; category: string | null }> | null;
  /** The winning five cards, to highlight on the board and in the hands. */
  bestCards: Set<Card> | null;
  /** Board position that gets the slow, dramatic reveal (the last card of a runout). */
  dramaticIndex: number | null;
  /** Seat → chance to win (0…1) while an all-in runout is still coming. */
  equity: Map<number, number> | null;
  skip: () => void;
}

type RunoutEquity = { board: number; shares: Record<number, number> }[];

interface Phase {
  handNo: number;
  view: HandView;
  equity: RunoutEquity | null;
  boardCount: number;
  revealed: boolean;
  winners: boolean;
  dramaticIndex: number | null;
}

// Whether a showdown is being played out on the felt right now, for things that should wait for
// it (the tournament winner's screen comes after the last hand has been shown).
let presenting = false;
const listeners = new Set<() => void>();
function setPresenting(next: boolean) {
  if (presenting === next) return;
  presenting = next;
  for (const l of listeners) l();
}
function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => void listeners.delete(listener);
}
export function useShowdownPresenting(): boolean {
  return useSyncExternalStore(subscribe, () => presenting);
}

/** Board cards dealt before anyone's hand was shown (the rest is an all-in runout). */
function boardBeforeReveal(view: HandView): number {
  let count = 0;
  for (const e of view.log) {
    if (e.t === 'reveal') return count;
    if (e.t === 'street') count += e.cards.length;
  }
  return view.board.length;
}

export function useShowdown(opts: {
  tournamentId: string | undefined;
  liveHandNo: number | null;
  liveBoard: number;
  previousHandNo: number | null;
  viewerId: string | undefined;
}): ShowdownView {
  const { tournamentId, liveHandNo, liveBoard, previousHandNo, viewerId } = opts;
  const watched = useRef(new Set<number>());
  const presented = useRef(0);
  const timers = useRef<number[]>([]);
  const [phase, setPhase] = useState<Phase | null>(null);

  // Remember which hands this page saw being played.
  useEffect(() => {
    if (liveHandNo !== null) watched.current.add(liveHandNo);
  }, [liveHandNo, liveBoard]);

  const clear = () => {
    for (const id of timers.current) window.clearTimeout(id);
    timers.current = [];
  };
  const skip = useCallback(() => {
    clear();
    setPhase(null);
  }, []);
  useEffect(() => clear, []);

  useEffect(() => {
    if (!tournamentId || previousHandNo === null) return;
    if (!watched.current.has(previousHandNo) || presented.current >= previousHandNo) return;
    presented.current = previousHandNo;
    let cancelled = false;
    void api
      .get<{ view: HandView; equity: RunoutEquity | null }>(`/api/tournaments/${tournamentId}/hands/${previousHandNo}`)
      .then(({ view, equity }) => {
        if (cancelled) return;
        clear();
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const showdown = view.players.some((p) => p.revealed);
        const start = showdown ? boardBeforeReveal(view) : view.board.length;
        const full = view.board.length;
        const base: Phase = { handNo: view.handNo, view, equity, boardCount: start, revealed: false, winners: false, dramaticIndex: null };
        const at = (ms: number, update: (p: Phase) => Phase) => {
          timers.current.push(window.setTimeout(() => setPhase((p) => (p && p.handNo === view.handNo ? update(p) : p)), ms));
        };
        if (reduced) {
          setPhase({ ...base, boardCount: full, revealed: true, winners: true });
          at(3500, () => null as unknown as Phase);
          timers.current.push(window.setTimeout(() => setPhase(null), 3500));
          return;
        }
        setPhase(base);
        let t = 0;
        if (showdown) {
          t += 600;
          at(t, (p) => ({ ...p, revealed: true }));
        }
        // The runout: flop together, then the turn, then the river slowly.
        let count = start;
        // With chances to win on show, give a moment to read them before each card.
        const read = equity ? 900 : 0;
        while (count < full) {
          const next = count === 0 ? 3 : count + 1;
          t += (count === 0 ? 1000 : next === full ? 1700 : 1400) + read;
          const c = next;
          at(t, (p) => ({ ...p, boardCount: c, dramaticIndex: c === full && full > start && c >= 4 ? c - 1 : p.dramaticIndex }));
          count = next;
        }
        t += full > start ? 1300 : showdown ? 900 : 300;
        at(t, (p) => ({ ...p, winners: true }));
        t += showdown ? 5000 : 2200;
        timers.current.push(window.setTimeout(() => setPhase((p) => (p && p.handNo === view.handNo ? null : p)), t));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [tournamentId, previousHandNo]);

  // Done once the winners are on show (the result may linger; nothing waits for that).
  useEffect(() => {
    setPresenting(phase !== null && !phase.winners);
  }, [phase]);
  useEffect(() => () => setPresenting(false), []);

  if (!phase) return { active: false, handNo: null, hand: null, potTotal: 0, winners: null, bestCards: null, dramaticIndex: null, equity: null, skip };
  const v = phase.view;
  const hand: HandView = {
    ...v,
    board: v.board.slice(0, phase.boardCount),
    // Until the reveal, shown hands are face down (own cards are always visible to oneself).
    players: v.players.map((p) => (phase.revealed || p.userId === viewerId || !p.revealed ? p : { ...p, hole: null })),
    toAct: null,
  };
  const awarded = v.result?.awarded ?? {};
  const potTotal = Object.values(awarded).reduce((s, n) => s + n, 0);
  let winners: ShowdownView['winners'] = null;
  let bestCards: Set<Card> | null = null;
  if (phase.winners) {
    winners = new Map();
    for (const [seat, amount] of Object.entries(awarded)) {
      if (amount > 0) winners.set(Number(seat), { amount, category: v.result?.hands[Number(seat)]?.category ?? null });
    }
    const best = [...winners.keys()].flatMap((seat) => v.result?.hands[seat]?.best5 ?? []);
    if (best.length > 0) bestCards = new Set(best);
  }
  const stage = phase.revealed && !phase.winners ? phase.equity?.find((e) => e.board === phase.boardCount) : undefined;
  const equity = stage ? new Map(Object.entries(stage.shares).map(([seat, share]) => [Number(seat), share])) : null;
  return { active: true, handNo: phase.handNo, hand, potTotal, winners, bestCards, dramaticIndex: phase.dramaticIndex, equity, skip };
}
